import { useEffect, useState } from "react";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import type { Alert } from "@/services/api/guardiansv2/types";

/**
 * Resolve the selected alert for the side panel. Prefers the already-loaded
 * list row; falls back to a fetch-by-id so deep links open even when the alert
 * is outside the current page/time window.
 */
export function useSelectedAlert(
  alertId: string | null,
  alerts: Alert[]
): { alert: Alert | null; loading: boolean } {
  const fromList = alertId ? alerts.find((a) => a._id === alertId) ?? null : null;
  const [fetched, setFetched] = useState<Alert | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!alertId || fromList) {
      setFetched(null);
      return;
    }
    let mounted = true;
    setLoading(true);
    GuardiansV2API.getAlert(alertId)
      .then((alert) => {
        if (mounted) setFetched(alert);
      })
      .catch(() => {
        if (mounted) setFetched(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [alertId, fromList]);

  return { alert: fromList ?? fetched, loading };
}
